import React, { useEffect, useRef } from 'react';
import { getOffer } from '../trainingOffers';
import { getOfferPath } from '../trainingRoutes';
import { formatDuration, formatTotal } from '../trainingFormat';

const EMPTY_ANSWERS = Object.freeze({
  goal: '',
  time: '',
  experience: '',
});

const COMPARATOR_STEPS = Object.freeze([
  {
    key: 'goal',
    labelKey: 'goalLabel',
    progressKey: 'reasonGoal',
    name: 'training-goal',
    optionsKey: 'goals',
    variant: 'goals',
  },
  {
    key: 'time',
    labelKey: 'timeLabel',
    progressKey: 'reasonTime',
    name: 'training-time',
    optionsKey: 'times',
    variant: 'times',
  },
  {
    key: 'experience',
    labelKey: 'experienceLabel',
    progressKey: 'reasonExperience',
    name: 'training-experience',
    optionsKey: 'experiences',
    variant: 'experiences',
  },
]);

function ComparatorQuestion({
  step,
  label,
  name,
  options,
  value,
  variant,
  onChange,
}) {
  return (
    <div className="training-comparator__question">
      <fieldset className="training-comparator__fieldset">
        <legend className="training-comparator__legend" tabIndex={-1}>
          <span className="training-comparator__step" aria-hidden="true">{step}</span>
          {label}
        </legend>
        <div className={`training-comparator__options training-comparator__options--${variant}`}>
          {options.map((option) => (
            <label className="training-comparator__option" key={option.value}>
              <input
                className="training-comparator__radio"
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
              />
              <span className="training-comparator__option-body">
                <span>
                  <strong>{option.label}</strong>
                  {option.description ? <small>{option.description}</small> : null}
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

export default function TrainingComparator({
  locale,
  content,
  answers = EMPTY_ANSWERS,
  step = 1,
  recommendation,
  onChange = () => {},
  onStepChange = () => {},
}) {
  const copy = content.comparator;
  const sectionRef = useRef();
  const resultTitleRef = useRef();
  const focusStepRef = useRef();
  const safeStep = step >= 1 && step <= 4 ? step : 1;
  const currentStep = COMPARATOR_STEPS[safeStep - 1];
  const isResultStep = safeStep === 4;
  const selectedGoal = copy.goals.find(({ value }) => value === answers.goal);
  const selectedTime = copy.times.find(({ value }) => value === answers.time);
  const selectedExperience = copy.experiences.find(({ value }) => value === answers.experience);
  const recommendedOffer = recommendation ? getOffer(recommendation.offerKey) : undefined;
  const recommendedCard = recommendedOffer
    ? content.offerCards.find(({ key }) => key === recommendedOffer.key)
    : undefined;
  const isComplete = Boolean(
    recommendedOffer
    && recommendedCard
    && selectedGoal
    && selectedTime
    && selectedExperience,
  );
  const showResult = isResultStep && isComplete;
  const stepCounter = copy.stepCounter
    .replace('{current}', String(safeStep))
    .replace('{total}', String(COMPARATOR_STEPS.length));

  useEffect(() => {
    const targetStep = focusStepRef.current;

    if (!targetStep) {
      return;
    }

    focusStepRef.current = null;

    if (targetStep <= COMPARATOR_STEPS.length) {
      const legend = sectionRef.current?.querySelector('.training-comparator__legend');
      legend?.focus();
    } else {
      resultTitleRef.current?.focus();
    }
  }, [safeStep]);

  const goToStep = (nextStep) => {
    focusStepRef.current = nextStep;
    onStepChange(nextStep);
  };

  const updateAnswer = (key, value) => {
    if (answers[key] === value) {
      return;
    }

    const nextAnswers = { ...answers, [key]: value };
    const currentIndex = COMPARATOR_STEPS.findIndex((item) => item.key === key);

    COMPARATOR_STEPS.slice(currentIndex + 1).forEach(({ key: laterKey }) => {
      nextAnswers[laterKey] = '';
    });

    onChange(nextAnswers);
  };

  const next = () => {
    if (!currentStep || !answers[currentStep.key] || safeStep >= 4) {
      return;
    }

    goToStep(safeStep + 1);
  };

  const back = () => {
    if (safeStep > 1) {
      goToStep(safeStep - 1);
    }
  };

  const reset = () => {
    onChange({ ...EMPTY_ANSWERS });
    goToStep(1);
  };

  return (
    <section
      className="training-comparator"
      aria-labelledby="training-comparator-title"
      ref={sectionRef}
    >
      <div className="training-comparator__intro">
        <p className="training-comparator__eyebrow">{copy.eyebrow}</p>
        <h2 className="training-comparator__title" id="training-comparator-title">
          {copy.title}
        </h2>
        <p className="training-comparator__introduction">{copy.introduction}</p>
        <p className="training-comparator__disclaimer">{copy.disclaimer}</p>
      </div>

      <div className={`training-comparator__layout${isResultStep ? ' training-comparator__layout--result' : ''}`}>
        {!isResultStep && currentStep ? (
          <form
            className="training-comparator__form"
            onSubmit={(event) => {
              event.preventDefault();
              next();
            }}
          >
            <div className="training-comparator__step-bar">
              <p className="training-comparator__step-counter" aria-live="polite">
                {stepCounter}
              </p>
              <div className="training-comparator__progress-track" aria-hidden="true">
                {COMPARATOR_STEPS.map((item, index) => (
                  <span
                    key={item.key}
                    data-active={index + 1 <= safeStep ? 'true' : 'false'}
                  />
                ))}
              </div>
            </div>

            <ComparatorQuestion
              key={safeStep}
              step={safeStep}
              label={copy[currentStep.labelKey]}
              name={currentStep.name}
              options={copy[currentStep.optionsKey]}
              value={answers[currentStep.key]}
              variant={currentStep.variant}
              onChange={(value) => updateAnswer(currentStep.key, value)}
            />

            <div className="training-comparator__actions">
              {safeStep > 1 ? (
                <button
                  className="training-comparator__back"
                  type="button"
                  onClick={back}
                >
                  {copy.back}
                </button>
              ) : <span aria-hidden="true" />}
              <button
                className="training-comparator__next"
                type="submit"
                disabled={!answers[currentStep.key]}
              >
                {safeStep === COMPARATOR_STEPS.length ? copy.seeResult : copy.next}
              </button>
            </div>
          </form>
        ) : null}

        <aside
          className="training-comparator__result"
          aria-live="polite"
          aria-atomic="true"
        >
          {showResult ? (
            <div className="training-comparator__result-card" data-state="complete">
              <p className="training-comparator__result-eyebrow">{copy.resultEyebrow}</p>
              <h3
                className="training-comparator__result-title"
                ref={resultTitleRef}
                tabIndex={-1}
              >
                {recommendedCard.name}
              </h3>
              <ul className="training-comparator__result-facts">
                <li>{formatDuration(recommendedOffer.duration, locale)}</li>
                <li>{formatTotal(recommendedOffer.total)}</li>
              </ul>
              <p className="training-comparator__result-description">
                {recommendedCard.positioning}
              </p>
              <div className="training-comparator__why">
                <p className="training-comparator__why-title">{copy.whyTitle}</p>
                <ul className="training-comparator__why-list">
                  <li>
                    <span>{copy.reasonGoal}</span>
                    <strong>{selectedGoal.label}</strong>
                  </li>
                  <li>
                    <span>{copy.reasonTime}</span>
                    <strong>{selectedTime.label}</strong>
                  </li>
                  <li>
                    <span>{copy.reasonExperience}</span>
                    <strong>{selectedExperience.label}</strong>
                  </li>
                </ul>
              </div>
              {recommendation.isClosest ? (
                <p className="training-comparator__closest">{copy.closestNote}</p>
              ) : null}
              <a
                className="training-comparator__cta"
                href={getOfferPath(locale, recommendedOffer.key)}
              >
                {copy.cta}
              </a>
              <a className="training-comparator__compare" href="#catalogue-offers">
                {copy.compareCta}
              </a>
              <div className="training-comparator__result-secondary">
                <button
                  className="training-comparator__edit"
                  type="button"
                  onClick={() => goToStep(1)}
                >
                  {copy.edit}
                </button>
                <button
                  className="training-comparator__reset"
                  type="button"
                  onClick={reset}
                >
                  {copy.reset}
                </button>
              </div>
            </div>
          ) : (
            <div className="training-comparator__result-card" data-state="pending">
              <p className="training-comparator__result-eyebrow">{copy.pendingEyebrow}</p>
              <h3 className="training-comparator__result-title">{copy.pendingTitle}</h3>
              <p className="training-comparator__result-description">{copy.pendingBody}</p>
              <ol className="training-comparator__progress">
                {COMPARATOR_STEPS.map((item, index) => {
                  const itemStep = index + 1;

                  return (
                    <li
                      key={item.key}
                      data-complete={itemStep < safeStep ? 'true' : 'false'}
                      data-current={itemStep === safeStep ? 'true' : 'false'}
                    >
                      <span className="training-comparator__progress-step" aria-hidden="true">
                        {itemStep}
                      </span>
                      {copy[item.progressKey]}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
